import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { Plus, Sparkles, ArrowLeft, ChevronRight } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { usePipelineRuns, useThemesForPicker } from '@/lib/admin/pipeline-queries';
import type { PipelineRunRow } from '@/lib/admin/pipeline-queries';
import { RUN_STATUS_LABEL, RUN_STATUS_CLS, runNeedsAttention } from '@/lib/admin/pipeline-labels';

export function TutorAdminPipelinePage() {
  const { data: runs, isLoading } = usePipelineRuns();
  const { data: themes } = useThemesForPicker();

  const themeTitle = useMemo(() => {
    const map = new Map((themes ?? []).map((t) => [t.id, t.title]));
    return (ids: string[]) => ids.map((id) => map.get(id) ?? '—');
  }, [themes]);

  const active = (runs ?? []).filter((r) => runNeedsAttention(r.status) || isRunning(r.status));
  const finished = (runs ?? []).filter((r) => !runNeedsAttention(r.status) && !isRunning(r.status));

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6">
      <Link
        to="/tutor/dashboard"
        className="text-ink-500 hover:text-ink-900 mb-4 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Admin
      </Link>

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="font-display text-accent-500 text-[11px] font-semibold uppercase tracking-[.14em]">
            Pipeline
          </p>
          <h1 className="font-display text-ink-900 mt-1 text-2xl font-extrabold tracking-tight">
            Génération d'exercices
          </h1>
          <p className="text-ink-500 mt-1 font-sans text-sm">
            {runs?.length ?? 0} run{(runs?.length ?? 0) !== 1 ? 's' : ''}
          </p>
        </div>
        <Link
          to="/tutor/admin/pipeline/new"
          className="bg-accent-400 font-display hover:bg-accent-500 inline-flex h-11 w-full shrink-0 items-center justify-center gap-2 rounded-xl px-5 text-sm font-bold text-white shadow-md transition-all active:scale-[.98] sm:w-auto"
        >
          <Plus className="h-4 w-4" />
          Nouveau pipeline
        </Link>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-ink-100 h-28 animate-pulse rounded-2xl" />
          ))}
        </div>
      ) : (runs?.length ?? 0) === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-6">
          {active.length > 0 && (
            <Section title="En cours">
              {active.map((run) => (
                <RunCard key={run.id} run={run} themeTitles={themeTitle(run.theme_ids)} />
              ))}
            </Section>
          )}
          {finished.length > 0 && (
            <Section title="Terminés">
              {finished.map((run) => (
                <RunCard key={run.id} run={run} themeTitles={themeTitle(run.theme_ids)} />
              ))}
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function isRunning(status: PipelineRunRow['status']): boolean {
  return (
    status === 'scenarios_generating' ||
    status === 'scenarios_auditing' ||
    status === 'matching' ||
    status === 'generating'
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="font-display text-ink-400 mb-2 text-[11px] font-bold uppercase tracking-widest">
        {title}
      </p>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

/** Chaque run mène à sa page de détail. */
function runLink(run: PipelineRunRow): { to: string; params: { runId: string } } {
  return { to: '/tutor/admin/pipeline/$runId', params: { runId: run.id } };
}

function RunCard({ run, themeTitles }: { run: PipelineRunRow; themeTitles: string[] }) {
  const link = runLink(run);
  const themesLabel =
    themeTitles.length <= 1
      ? (themeTitles[0] ?? '—')
      : `${themeTitles[0]} +${themeTitles.length - 1}`;
  const progress = run.total_jobs > 0 ? Math.round((run.done_jobs / run.total_jobs) * 100) : 0;

  const inner = (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={cn(
            'font-display inline-flex h-6 items-center rounded-full border px-2.5 text-[11px] font-bold',
            RUN_STATUS_CLS[run.status],
          )}
        >
          {RUN_STATUS_LABEL[run.status]}
        </span>
        {run.status === 'generating' && (
          <span className="text-ink-500 font-mono text-[11px]">
            {run.done_jobs}/{run.total_jobs} exos
          </span>
        )}
        <ChevronRight className="text-ink-300 ml-auto h-4 w-4" />
      </div>

      <h3 className="font-display text-ink-900 mt-2 line-clamp-1 text-[15px] font-extrabold">
        {themesLabel}
      </h3>

      {run.status === 'generating' && (
        <div className="bg-ink-100 mt-2 h-1.5 w-full overflow-hidden rounded-full">
          <div className="bg-primary-500 h-full rounded-full" style={{ width: `${progress}%` }} />
        </div>
      )}

      <div className="border-ink-100 mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t pt-2.5">
        <span className="text-ink-500 font-sans text-xs">
          {run.launcher?.display_name ?? 'Admin'}
        </span>
        <span className="text-ink-300">·</span>
        <span className="text-ink-500 font-mono text-xs">{run.total_cost_eur.toFixed(2)} €</span>
        <span className="text-ink-300">·</span>
        <span className="text-ink-400 font-sans text-xs">
          {new Date(run.created_at).toLocaleDateString('fr-FR')}
        </span>
      </div>
      {run.status === 'failed' && run.error && (
        <p className="text-accent-600 mt-2 line-clamp-2 font-sans text-xs">{run.error}</p>
      )}
    </>
  );

  return (
    <Link
      to={link.to}
      params={link.params}
      className={cn(
        'border-ink-200 hover:border-primary-200 block rounded-2xl border bg-white p-4',
        'transition-all hover:shadow-md active:scale-[.99]',
      )}
    >
      {inner}
    </Link>
  );
}

function EmptyState() {
  return (
    <div className="border-ink-200 flex flex-col items-center rounded-2xl border border-dashed bg-white p-10 text-center">
      <Sparkles className="text-ink-300 h-8 w-8" />
      <p className="font-display text-ink-700 mt-3 text-sm font-bold">Aucun pipeline lancé</p>
      <p className="text-ink-500 mt-1 font-sans text-xs">
        Lance un run pour générer des exercices à partir d'un thème.
      </p>
      <Link
        to="/tutor/admin/pipeline/new"
        className="bg-accent-400 font-display hover:bg-accent-500 mt-4 inline-flex h-11 items-center gap-2 rounded-xl px-5 text-sm font-bold text-white transition-all active:scale-[.98]"
      >
        <Plus className="h-4 w-4" />
        Nouveau pipeline
      </Link>
    </div>
  );
}
